import React from 'react'
import DateRangeFilter from '../../../components/DateRangeFilter'
import CustomSelect from '../../../components/CustomSelect/CustomSelect'
import { OperationsSkeleton } from '../../../components/Skeleton/Skeleton'
import Modal from '../../../components/Modal'
import PendingWorkLogRequests from './PendingWorkLogRequests'

const formatDate = (date) => {
  if (!date) return ''
  if (typeof date === 'string') {
    const parts = date.split('T')[0].split('-')
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    }
  }
  return new Date(date).toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

const formatNumberWithSpaces = (val) => {
  if (val === null || val === undefined || val === '') return ''
  const clean = val.toString().replace(/[^\d.]/g, '')
  const parts = clean.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return parts.join('.')
}

export default function OperationsFeed({
  displayedRecords,
  loading,
  isAdmin,
  userPermissions,
  profile,
  activePvz,
  employees,
  records,
  salaryConfig,
  customRules,
  filterStartDate,
  setFilterStartDate,
  filterEndDate,
  setFilterEndDate,
  filterEmployee,
  setFilterEmployee,
  filterType,
  setFilterType,
  filterSearch,
  setFilterSearch,
  summary,
  baseActions,
  getTypeInfo,
  shiftMarkers,
  handleQuickAdd,
  setShowAddMenu,
  setQuickAddContext,
  handleEdit,
  itemToDelete,
  setItemToDelete,
  confirmDelete,
  todayIso,
  yesterdayIso,
  monthStartIso,
  monthEndIso,
  quarterStartIso,
  quarterEndIso,
  selectedPvzIds,
  userPvzs,
  getPvzColor,
  getPvzShortName,
  getPvzFullName,
  pendingChangeRequests = [],
  handleResolveChangeRequest
}) {
  const renderPvzBadge = (pvzId) => {
    if (!pvzId) return null
    if (!selectedPvzIds || selectedPvzIds.length <= 1) return null
    const shortName = getPvzShortName ? getPvzShortName(pvzId) : ''
    const pvzColor = getPvzColor ? getPvzColor(pvzId) : 'var(--text-tertiary)'
    const pvzFullName = getPvzFullName ? getPvzFullName(pvzId) : 'Пункт выдачи'
    return (
      <span 
        style={{
          fontSize: '9px',
          fontWeight: 800,
          color: pvzColor,
          background: `color-mix(in srgb, ${pvzColor} 8%, var(--bg-secondary))`,
          border: `1px solid color-mix(in srgb, ${pvzColor} 30%, var(--surface-border))`,
          borderRadius: '4px',
          padding: '1px 4px',
          marginLeft: '8px',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center'
        }}
        title={`Пункт: ${pvzFullName}`}
      >
        {shortName}
      </span>
    )
  }

  return (
    <>
      <PendingWorkLogRequests 
        pendingRequests={pendingChangeRequests} 
        onResolve={handleResolveChangeRequest} 
      />

      {/* Кнопка добавления операции */}
      <button
        type="button"
        data-tour="operations-add"
        onClick={() => { setQuickAddContext(null); setShowAddMenu(true) }}
        style={{
          width: '100%', padding: '14px 20px', border: 'none', borderRadius: '14px', cursor: 'pointer',
          background: 'linear-gradient(135deg, var(--wb-primary), #9333ea)', color: 'white',
          fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '10px', boxShadow: '0 4px 16px rgba(203,17,171,0.3)', transition: 'all 0.2s',
          marginBottom: '4px',
        }}
      >
        <span style={{ fontSize: '20px' }}>＋</span> Добавить операцию
      </button>

      {/* Подвкладки по типам */}
      <div data-tour="operations-tabs" style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x', marginBottom: '12px' }} onTouchMove={e => e.stopPropagation()}>
        <button 
          type="button"
          onClick={() => setFilterType('all')} 
          style={{
            padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.2s',
            background: filterType === 'all' ? 'var(--wb-primary)' : 'var(--surface)',
            color: filterType === 'all' ? 'white' : 'var(--text-secondary)',
            boxShadow: filterType === 'all' ? '0 2px 8px rgba(203,17,171,0.25)' : 'none',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
          Все
        </button>
        {baseActions.map(a => (
          <button 
            type="button"
            key={a.id} 
            onClick={() => setFilterType(filterType === a.id ? 'all' : a.id)} 
            style={{
              padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.2s',
              background: filterType === a.id ? a.color : 'var(--surface)',
              color: filterType === a.id ? 'white' : 'var(--text-secondary)',
              boxShadow: filterType === a.id ? `0 2px 8px ${a.color}40` : 'none',
            }}
          >
            {a.title} {summary ? `(${summary[a.id] || 0})` : ''}
          </button>
        ))}
        {isAdmin && (
          <button 
            type="button"
            onClick={() => setFilterType(filterType === 'expense_var' ? 'all' : 'expense_var')} 
            style={{
              padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.2s',
              background: filterType === 'expense_var' ? 'var(--danger)' : 'var(--surface)',
              color: filterType === 'expense_var' ? 'white' : 'var(--text-secondary)',
            }}
          >
            Расходник {summary ? `(${formatNumberWithSpaces(summary.expense_var || 0)} ₽)` : ''}
          </button>
        )}
        {customRules.map(r => (
          <button 
            type="button"
            key={r.id} 
            onClick={() => setFilterType(filterType === `custom:${r.id}` ? 'all' : `custom:${r.id}`)} 
            style={{
              padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.2s',
              background: filterType === `custom:${r.id}` ? 'var(--warning)' : 'var(--surface)',
              color: filterType === `custom:${r.id}` ? 'white' : 'var(--text-secondary)',
            }}
          >
            {r.name}
          </button>
        ))}
      </div>

      {/* Фильтры */}
      <div className="operations-filters card" data-tour="operations-filters">
        <div style={{ width: '100%', maxWidth: '300px' }}>
          <DateRangeFilter
            startDate={filterStartDate} endDate={filterEndDate}
            onApply={(s, e) => { setFilterStartDate(s); setFilterEndDate(e) }}
            onReset={() => { setFilterStartDate(todayIso()); setFilterEndDate(todayIso()) }}
            quickPresets={[
              { label: 'Сегодня', getValue: () => [todayIso(), todayIso()] },
              { label: 'Вчера', getValue: () => [yesterdayIso(), yesterdayIso()] },
              { label: 'Этот месяц', getValue: () => [monthStartIso(), monthEndIso()] },
              { label: 'Этот квартал', getValue: () => [quarterStartIso(), quarterEndIso()] }
            ]}
            showQuickButtons
            shiftMarkers={shiftMarkers}
            onBadgeClick={handleQuickAdd}
          />
        </div>
        <div className="input-group" style={{ flex: 1, minWidth: '200px' }}>
          <input 
            type="text" 
            className="input" 
            placeholder="Поиск по операциям, коробкам, комментариям..." 
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
          />
        </div>
        {(isAdmin || userPermissions?.canManageOperations) && (
          <div style={{ maxWidth: '220px', minWidth: '180px' }}>
            <CustomSelect
              value={filterEmployee}
              onChange={setFilterEmployee}
              placeholder="Все сотрудники"
              options={[
                { value: '', label: 'Все сотрудники' },
                ...employees.filter(e => !e.is_legacy || (records || []).some(r => r.employee_id === e.id)).map(emp => ({
                  value: emp.id,
                  label: emp.is_legacy ? `${emp.full_name || emp.name} (Архив)` : (emp.full_name || emp.name || 'Неизвестно'),
                })),
              ]}
            />
          </div>
        )}
      </div>

      {/* Лента (Feed) */}
      <div className="operations-feed">
        {loading ? (
          <OperationsSkeleton />
        ) : displayedRecords.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div>
            <div className="empty-state-title">Нет операций</div>
            <div className="empty-state-text">По вашему запросу операции не найдены.</div>
          </div>
        ) : (
          <div className="feed-list">
            {displayedRecords.map(record => {
              const info = getTypeInfo(record.type)
              return (
                <div key={record.id} className="feed-item card">
                  <div className="feed-item-icon" style={{ background: `color-mix(in srgb, ${info.color} 15%, transparent)`, color: info.color }}>
                    {info.icon}
                  </div>
                  <div className="feed-item-content">
                    <div className="feed-item-header">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span className="feed-item-title">{info.title}</span>
                        {renderPvzBadge(record.pvz_id)}
                      </div>
                      <span className="feed-item-date">{formatDate(record.work_date)} {new Date(record.created_at).toLocaleTimeString('ru-RU', {hour: '2-digit', minute:'2-digit'})}</span>
                    </div>
                    {record.type === 'acceptance' && (
                      <div className="feed-item-details" style={{ marginTop: '8px', gap: '12px' }}>
                        <span className="feed-item-value">Вовремя: <strong>{record.count}</strong> шт.</span>
                        <span className="feed-item-value">С опозданием: <strong>{record.count_late || 0}</strong> шт.</span>
                      </div>
                    )}
                    <div className="feed-item-details">
                      {(isAdmin || userPermissions?.canManageOperations) && <span className="feed-item-employee">{record.employee_name}</span>}
                      {record.type === 'returns' ? (
                        <>
                          <span className="feed-item-value">Коробка: <strong>{record.box_number}</strong></span>
                          {record.count > 0 && <span className="feed-item-value" style={{ marginLeft: '8px' }}>Товаров: <strong>{record.count}</strong> шт.</span>}
                          {(record.start_time || record.end_time) && (
                            <span className="feed-item-time" style={{ marginLeft: '8px' }}>{record.start_time || '--:--'} - {record.end_time || '--:--'}</span>
                          )}
                        </>
                      ) : record.type === 'expense_var' ? (
                        <>
                          <span className="feed-item-value">Категория: <strong>{record.box_number || 'Хозтовары'}</strong></span>
                          <span className="feed-item-value" style={{ marginLeft: '8px' }}>Сумма: <strong>{formatNumberWithSpaces(record.count)} ₽</strong></span>
                          {record.count_late > 0 && <span className="feed-item-value" style={{ marginLeft: '8px' }}>Кол-во: <strong>{record.count_late} шт.</strong></span>}
                        </>
                      ) : (
                        record.type !== 'acceptance' && (
                          <span className="feed-item-value">Количество: <strong>{record.count}</strong> {info.isCustom && info.rule?.type === 'percent' ? '₽' : 'шт.'}</span>
                        )
                      )}
                    </div>
                    {record.comment && <div className="feed-item-comment">{record.comment}</div>}
                    
                    {/* Кнопки удаления/редактирования */}
                    {((isAdmin || userPermissions?.canManageOperations) || (new Date(record.created_at).toDateString() === new Date().toDateString())) && (
                      <div className="feed-item-actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => handleEdit(record)}>Редактировать</button>
                        <button type="button" className="btn btn-ghost btn-sm text-danger" onClick={() => setItemToDelete(record.id)}>Удалить</button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Модалка подтверждения удаления */}
      <Modal isOpen={!!itemToDelete} onClose={() => setItemToDelete(null)} title="Удаление записи" isBottomSheet={true}>
        <div style={{ padding: '10px 0 24px 0', color: 'var(--text-secondary)' }}>
          Вы уверены, что хотите удалить эту операцию? Это действие нельзя отменить.
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setItemToDelete(null)}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" style={{ flex: 1, background: 'var(--danger)' }} onClick={confirmDelete}>
            Удалить
          </button>
        </div>
      </Modal>
    </>
  )
}
